import React, { useState, useTransition } from "react";
import { useTranslation } from 'react-i18next';
import { motion } from "framer-motion";
import Button from '../components/Button';

import { ArrowRight, Bus, Briefcase, ShieldCheck, Clock, Users, MapPin, Truck } from "lucide-react";
import { Link } from "react-router-dom"; // React Router for links

import bags from '../images/bags.jpg';
import forgiens from '../images/forgiens.png';
import haj from '../images/haj.png';
import passengers from '../images/passengers.jpg';
import introImg from '../images/intro_img2.jpeg';
import whyus from '../images/whyus.jpg';

export default function Home() {
    const { t, i18n } = useTranslation();
    const language = i18n.language;
    const isRTL = language === "ar" || language === "he";

    const services = [
        {
            icon: <Users className="w-10 h-10 text-primary" />,
            title: "passengerTransport",
            desc: "passengerTransportDesc",
            // image: "https://images.unsplash.com/photo-1570125909232-eb263c188f7e?auto=format&fit=crop&q=80&w=800"
            image: passengers
        },
        {
            icon: <Users className="w-10 h-10 text-primary" />,
            title: "hajService",
            desc: "hajDesc",
            // image: "https://images.unsplash.com/photo-1570125909232-eb263c188f7e?auto=format&fit=crop&q=80&w=800"
            image: haj
        },
        {
            icon: <Briefcase className="w-10 h-10 text-primary" />,
            title: "vipService",
            desc: "vipDesc",
            // image: "https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?auto=format&fit=crop&q=80&w=800"
            image: forgiens
        },
        {
            icon: <Truck className="w-10 h-10 text-primary" />,
            title: "cargoTransport",
            desc: "cargoTransportDesc",
            // image: "https://images.unsplash.com/photo-1494515843206-f3117d3f51b7?auto=format&fit=crop&q=80&w=800"
            image: bags
        }
    ];

    return (
        <div className="min-h-screen bg-background" dir={t('dir')}>
            {/* Hero Section */}
            <section className="relative h-[600px] md:h-[700px] flex items-center overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center z-0 transition-transform duration-1000 hover:scale-105"
                     style={{ backgroundImage: `url('https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?auto=format&fit=crop&q=80&w=1920')` }}                    
                />
                <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-slate-900/40 z-10" />
                <div className="container relative z-20 px-4 mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="max-w-3xl"
                    >
                        <div className="inline-block px-4 py-1.5 rounded-full bg-accent/20 border border-accent/40 text-accent font-bold text-sm mb-6 backdrop-blur-sm">
                            {t('withYou')}
                        </div>
                        <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight mb-6">
                            {t('tripStart')}
                            <span className="text-accent block mt-2">
                                {t('saftyTitle')}
                            </span>
                        </h1>
                        <p className="text-xl text-slate-200 mb-10 max-w-2xl leading-relaxed">{t('welcomeSubtitle')}</p>
                        <div className="flex flex-col sm:flex-row gap-4">
                            {/* <Button
                                size="lg"
                                className="bg-accent hover:bg-accent/90 text-slate-900 font-bold h-14 px-8 text-lg shadow-lg shadow-accent/20"
                                onClick={() => window.open("https://book.abdobus.com/", "_blank")}
                            >
                                {t('bookNow')}
                            </Button> */}
                            <Link to="/services">
                                <Button
                                    size="lg"
                                    variant="outline"
                                    className="bg-white/10 border-white text-white hover:bg-white/20 h-14 px-8 text-lg backdrop-blur-sm"
                                >
                                    {t('readMore')}
                                </Button>
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Features Grid */}
            <section className="py-20 bg-slate-50 dark:bg-slate-900">
                <div className="container px-4 mx-auto">
                    <div className="text-center max-w-2xl mx-auto mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">{t('services')}</h2>
                        <div className="h-1.5 w-20 bg-accent mx-auto rounded-full" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {services.map((service, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                viewport={{ once: true }}
                                className="group bg-white dark:bg-slate-800 rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 border border-slate-100 dark:border-slate-700"
                            >
                                <div className="h-48 overflow-hidden relative">
                                    <img
                                        src={service.image}
                                        alt={t(service.title)}
                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                    />
                                    <div className="absolute inset-0 bg-primary/20 group-hover:bg-transparent transition-colors duration-300" />
                                </div>
                                <div className="p-8 relative">
                                    <div className="absolute -top-10 right-8 bg-white dark:bg-slate-700 p-4 rounded-2xl shadow-lg">{service.icon}</div>
                                    <h3 className="text-xl font-bold mb-3 text-slate-900 dark:text-white mt-4">{t(service.title)}</h3>
                                    <p className="text-muted-foreground leading-relaxed mb-4">{t(service.desc)}</p>
                                    <Link to="/services">
                                        <span className="inline-flex items-center text-primary font-bold hover:text-accent cursor-pointer">
                                            {t('readMore')}
                                            {isRTL ? <ArrowRight className="mr-2 h-4 w-4 rotate-180" /> : <ArrowRight className="ml-2 h-4 w-4" />}
                                        </span>
                                    </Link>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="py-24 bg-primary text-white relative overflow-hidden">
                {/* Abstract pattern overlay */}
                <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 2px 2px, white 1px, transparent 0)", backgroundSize: "40px 40px" }} />

                <div className="container px-4 mx-auto relative z-10">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <h2 className="text-3xl md:text-5xl font-bold mb-8 leading-tight">
                                {t('whyUs')}
                            </h2>
                            <div className="space-y-6">
                                {[
                                    { icon: Bus, text: t('newBuses') },
                                    { icon: ShieldCheck, text: t('highSafty') },
                                    { icon: Users, text: t('profDrivers') },
                                    { icon: Clock, text: t('timeDiscipline') },
                                ].map((item, i) => (
                                    <div key={i} className="flex items-center gap-4 bg-white/10 p-4 rounded-xl hover:bg-white/20 transition-colors">
                                        <div className="w-12 h-12 rounded-full bg-accent text-primary-foreground flex items-center justify-center shrink-0">
                                            <item.icon className="w-6 h-6" />
                                        </div>
                                        <span className="text-lg font-medium">{item.text}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="relative">
                            <div className="absolute -inset-4 bg-accent/30 rounded-full blur-3xl" />
                            <img
                                // src="https://images.unsplash.com/photo-1557223562-6c77ef16210f?auto=format&fit=crop&q=80&w=800"
                                src={whyus}
                                alt="Professional Driver"
                                className="relative rounded-3xl shadow-2xl border-4 border-white/20 transform rotate-3 
                                hover:rotate-0 transition-transform duration-500"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Call to Action */}
            <section className="py-20 bg-slate-50 dark:bg-slate-900">
                <div className="container px-4 mx-auto">
                    <div className="bg-slate-900 rounded-[2.5rem] p-12 md:p-20 text-center relative overflow-hidden shadow-2xl">
                        <div className="absolute top-0 left-0 w-full h-full opacity-30 bg-center bg-cover mix-blend-overlay" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&q=80&w=1200')" }} />

                        <div className="relative z-10">
                            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
                                {t('withYou')}
                            </h2>
                            <p className="text-slate-300 text-lg md:text-xl mb-10 max-w-2xl mx-auto">
                                {/* {language === 'ar' ? "احجز تذكرتك الآن واستمتع برحلة آمنة ومريحة مع أفضل شركة نقل في المنطقة" : "Book your ticket now and enjoy a safe and comfortable trip with the best transport company in the region"} */}
                                {t('welcomeSubtitle')}
                            </p>
                            {/* <Button
                                size="lg"
                                className="bg-accent hover:bg-accent/90 text-slate-900 font-bold h-16 px-10 text-xl rounded-full shadow-lg shadow-accent/20 transform hover:scale-105 transition-all"
                                onClick={() => window.open('https://book.abdobus.com/', '_blank')}
                            >
                                {t('bookNow')}
                            </Button> */}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
